import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Star, Send, ThumbsUp, Heart, Lightbulb, Laugh, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import UserAvatar from './avatars/UserAvatar';
import BookwormCard from './avatars/BookwormCard';
import TranslateButton from './TranslateButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// How many reviews to show per page. Picked 7 per the 2026-05 user request —
// long enough to feel substantial, short enough that the book page doesn't
// turn into an endless scroll.
const REVIEWS_PER_PAGE = 7;

const REACTION_TYPES = [
  { key: 'like', icon: ThumbsUp, label: 'Like', color: 'text-blue-400' },
  { key: 'love', icon: Heart, label: 'Love', color: 'text-red-400' },
  { key: 'insightful', icon: Lightbulb, label: 'Insightful', color: 'text-yellow-400' },
  { key: 'funny', icon: Laugh, label: 'Funny', color: 'text-green-400' },
];

const ReviewSection = ({ bookId, onReviewSubmitted }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [page, setPage] = useState(1);
  // Client-side sort — 'newest' (backend default) | 'highest' | 'helpful'.
  // 'helpful' = total reactions across all 4 reaction types (thumbs/heart/
  // idea/laugh) — the closest signal we have to community endorsement
  // without a separate "helpful" upvote endpoint.
  const [sortBy, setSortBy] = useState('newest');
  // Top-of-list anchor so we scroll back to the first review when the admin
  // (or a returning reader) flips pages — feels much better than the new
  // page rendering at whatever scroll position they were on.
  const listTopRef = useRef(null);
  const { toast } = useToast();
  const userId = localStorage.getItem('userId') || 'demo_user';
  const userName = localStorage.getItem('userName') || 'Anonymous';
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review? This will remove it from the book page AND the landing page.')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      toast({ title: 'Review deleted' });
      fetchStats();
      if (onReviewSubmitted) onReviewSubmitted();
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: e.response?.data?.detail || 'Could not delete review',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchStats();
    setPage(1); // new book → start at page 1
  }, [bookId]);

  // Apply the active sort BEFORE pagination so page boundaries reflect the
  // user's chosen order. We sort a copy so the underlying `reviews` array
  // (used elsewhere for stats counts etc.) stays in its arrival order.
  const sortedReviews = useMemo(() => {
    if (sortBy === 'newest') return reviews; // backend already returns newest-first
    const copy = [...reviews];
    if (sortBy === 'highest') {
      copy.sort((a, b) => {
        const ra = Number(a.rating || 0);
        const rb = Number(b.rating || 0);
        if (rb !== ra) return rb - ra;
        // Tie-break by newest so two 5-star reviews stay in a sensible order
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    } else if (sortBy === 'helpful') {
      const totalReactions = (r) =>
        Object.values(r.reactions || {}).reduce(
          (sum, users) => sum + (Array.isArray(users) ? users.length : 0),
          0,
        );
      copy.sort((a, b) => {
        const diff = totalReactions(b) - totalReactions(a);
        if (diff !== 0) return diff;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    }
    return copy;
  }, [reviews, sortBy]);

  // Compute paginated slice. Memoized so a reaction-toggle re-render doesn't
  // re-slice on every render — keeps the list visually stable.
  const totalPages = Math.max(1, Math.ceil(sortedReviews.length / REVIEWS_PER_PAGE));
  const paginatedReviews = useMemo(() => {
    const start = (page - 1) * REVIEWS_PER_PAGE;
    return sortedReviews.slice(start, start + REVIEWS_PER_PAGE);
  }, [sortedReviews, page]);

  // If reviews shrink (e.g. an admin deletes one and the last page becomes
  // empty), snap back to a valid page. Without this guard the user would see
  // a blank list and have to manually press "Prev".
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Smooth-scroll to the top of the list when the page changes (but NOT on
  // initial mount).
  const isFirstPageRef = useRef(true);
  useEffect(() => {
    if (isFirstPageRef.current) {
      isFirstPageRef.current = false;
      return;
    }
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${bookId}`);
      setReviews(response.data);
      const userReview = response.data.find(r => r.user_id === userId);
      setHasReviewed(!!userReview);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleReaction = async (reviewId, reactionType) => {
    if (!token) {
      toast({ title: 'Please log in', description: 'Sign in to react to reviews', variant: 'destructive' });
      return;
    }
    try {
      const response = await axios.post(`${API}/reviews/${reviewId}/react`, {
        reaction_type: reactionType
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Update the review's reactions locally
      setReviews(prev => prev.map(r => {
        if (r.id === reviewId) {
          return { ...r, reactions: response.data.reactions };
        }
        return r;
      }));
    } catch {
      toast({ title: 'Error', description: 'Could not react to review', variant: 'destructive' });
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats/${bookId}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (hasReviewed) {
      toast({
        title: 'Already Reviewed',
        description: 'You have already reviewed this book. Only one review per user is allowed.',
        variant: 'destructive',
      });
      return;
    }
    
    if (rating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select a star rating (1-5 stars).',
        variant: 'destructive',
      });
      return;
    }

    if (!comment.trim()) {
      toast({
        title: 'Comment Required',
        description: 'Please write a review comment.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/reviews`,
        {
          book_id: bookId,
          rating: rating,
          comment: comment.trim()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast({
        title: 'Review Posted!',
        description: 'Thank you for your feedback.',
      });

      setRating(0);
      setComment('');
      setHasReviewed(true);
      // Jump to page 1 so the user immediately sees their newly-posted
      // review (the backend returns reviews sorted newest-first).
      setPage(1);
      
      // Update stats immediately from response
      if (response.data.updated_stats) {
        setStats(response.data.updated_stats);
      }
      
      fetchReviews();
      
      // Notify parent to refresh book data (for rating update on book details)
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (error) {
      console.error('Failed to post review:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to post review. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (count, interactive = false) => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      const filled = interactive 
        ? (hoverRating || rating) >= starValue
        : count >= starValue;

      return (
        <Star
          key={`star-${starValue}`}
          className={`h-6 w-6 cursor-pointer transition-colors ${
            filled ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'
          }`}
          onClick={() => interactive && setRating(starValue)}
          onMouseEnter={() => interactive && setHoverRating(starValue)}
          onMouseLeave={() => interactive && setHoverRating(0)}
        />
      );
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Stats */}
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Reader Reviews</h2>
        <div className="flex items-center justify-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="flex">{renderStars(Math.round(stats.average_rating))}</div>
            <span className="text-2xl font-bold text-white">{stats.average_rating.toFixed(1)}</span>
          </div>
          <span className="text-gray-400">({stats.total_reviews} reviews)</span>
        </div>
      </div>

      {/* Review Form */}
      <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 mb-8 border border-white/10">
        <h3 className="text-xl font-bold text-white mb-4">Write a Review</h3>
        
        {hasReviewed ? (
          <div className="text-center py-4">
            <p className="text-green-400 mb-2">✓ You have already reviewed this book</p>
            <p className="text-gray-500 text-sm">Only one review per user is allowed.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmitReview} className="space-y-4" data-testid="review-form">
            <div>
              <label className="text-white text-sm mb-2 block">Your Rating (Required)</label>
              <div className="flex space-x-1" data-testid="star-rating-input">
                {renderStars(rating, true)}
              </div>
            </div>
            
            <div>
              <label className="text-white text-sm mb-2 block">Your Review (Required)</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your thoughts about this book..."
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 min-h-32"
                disabled={loading}
                data-testid="review-comment-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || hasReviewed}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="submit-review-btn"
            >
              <Send className="mr-2 h-4 w-4" />
              {loading ? 'Posting...' : 'Post Review'}
            </Button>
          </form>
        )}
      </div>

      {/* Reviews List */}
      {reviews.length > 1 && (
        <div className="flex items-center justify-end gap-2 mb-3" data-testid="reviews-sort-bar">
          <label htmlFor="reviews-sort" className="text-xs text-gray-400">Sort by:</label>
          <select
            id="reviews-sort"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="bg-zinc-900 border border-white/10 hover:border-white/30 text-white text-xs rounded-md px-2 py-1 transition-colors"
            data-testid="reviews-sort-select"
          >
            <option value="newest">Newest</option>
            <option value="highest">Highest rated</option>
            <option value="helpful">Most helpful</option>
          </select>
        </div>
      )}

      <div ref={listTopRef} className="space-y-4">
        {reviews.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No reviews yet. Be the first to review!</p>
        ) : (
          paginatedReviews.map((review) => (
            <div
              key={review.id}
              className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10"
              data-testid={`review-card-${review.id}`}
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <BookwormCard
                    userId={review.user_id}
                    name={review.user_name}
                    avatarId={review.user_avatar_id}
                    avatarPhotoUrl={review.user_avatar_photo_url}
                  >
                    <UserAvatar
                      name={review.user_name}
                      avatarId={review.user_avatar_id}
                      avatarPhotoUrl={review.user_avatar_photo_url}
                      size="lg"
                      testId={`review-avatar-${review.id}`}
                      ringColor="ring-fuchsia-400/30"
                    />
                  </BookwormCard>
                  <div className="min-w-0">
                    <h4 className="text-white font-semibold truncate">{review.user_name}</h4>
                    <p className="text-gray-400 text-sm">
                      {new Date(review.timestamp).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex">{renderStars(review.rating)}</div>
                  {userRole === 'admin' && token && (
                    <button
                      type="button"
                      onClick={() => handleDeleteReview(review.id)}
                      className="text-gray-400 hover:text-red-400 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                      title="Delete review (admin)"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-200 leading-relaxed mb-2">{review.comment}</p>
              {review.comment && (
                <TranslateButton
                  text={review.comment}
                  sourceId={`review-${review.id}`}
                  className="mb-3"
                />
              )}

              {/* Reaction Buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                {REACTION_TYPES.map(({ key, icon: Icon, label, color }) => {
                  const reactionUsers = review.reactions?.[key] || [];
                  const hasReacted = reactionUsers.includes(userId);
                  const count = reactionUsers.length;
                  return (
                    <button
                      key={key}
                      onClick={() => handleReaction(review.id, key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        hasReacted
                          ? 'bg-white/15 border border-white/20 ' + color
                          : 'bg-white/5 border border-transparent text-gray-400 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`reaction-${key}-${review.id}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {count > 0 && <span>{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination — 7 reviews per page. Only renders when there's more
          than one page worth of reviews so books with few reviews don't
          show a lonely "Page 1 / 1" footer. */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between mt-5 pt-4 border-t border-white/10"
          data-testid="reviews-pagination"
        >
          <p className="text-xs text-gray-400">
            Showing{' '}
            <span className="text-white font-medium">
              {Math.min(reviews.length, (page - 1) * REVIEWS_PER_PAGE + 1)}
              –
              {Math.min(reviews.length, page * REVIEWS_PER_PAGE)}
            </span>{' '}
            of <span className="text-white font-medium">{reviews.length}</span> reviews
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-gray-200 transition-colors"
              data-testid="reviews-prev-page"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs text-gray-300" data-testid="reviews-page-indicator">
              Page <span className="text-white font-semibold">{page}</span> / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-gray-200 transition-colors"
              data-testid="reviews-next-page"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewSection;